/** 相对原图 0–1 的矩形（x,y 左上；w,h 宽高） */
export type NormalizedRect = {
  x: number
  y: number
  w: number
  h: number
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n))
}

export function clampNormalizedRect(r: NormalizedRect): NormalizedRect {
  const x = clamp01(r.x)
  const y = clamp01(r.y)
  const w = clamp01(Math.min(r.w, 1 - x))
  const h = clamp01(Math.min(r.h, 1 - y))
  return { x, y, w: Math.max(0.02, w), h: Math.max(0.02, h) }
}

/** 以中心为基准扩展并裁到目标宽高比（如角色卡 3:4） */
export function expandRectToAspect(rect: NormalizedRect, aspect: number): NormalizedRect {
  const cx = rect.x + rect.w / 2
  const cy = rect.y + rect.h / 2
  let w = rect.w
  let h = rect.h
  const current = w / h
  if (current > aspect) {
    h = w / aspect
  } else {
    w = h * aspect
  }
  let x = cx - w / 2
  let y = cy - h / 2
  if (x < 0) x = 0
  if (y < 0) y = 0
  if (x + w > 1) x = 1 - w
  if (y + h > 1) y = 1 - h
  return clampNormalizedRect({ x, y, w, h })
}

/** 浏览器 Face Detector（Chrome/Edge）；无则返回 null */
export async function detectFaceBounds(image: HTMLImageElement): Promise<NormalizedRect | null> {
  const FaceDetectorCtor = (
    globalThis as typeof globalThis & {
      FaceDetector?: new (opts?: { maxDetectedFaces?: number }) => {
        detect: (src: ImageBitmapSource) => Promise<
          ReadonlyArray<{ boundingBox: { x: number; y: number; width: number; height: number } }>
        >
      }
    }
  ).FaceDetector
  if (!FaceDetectorCtor) return null

  const nw = image.naturalWidth
  const nh = image.naturalHeight
  if (nw < 16 || nh < 16) return null

  try {
    const detector = new FaceDetectorCtor({ maxDetectedFaces: 1 })
    const faces = await detector.detect(image)
    const box = faces[0]?.boundingBox
    if (!box || box.width < 4 || box.height < 4) return null

    const fx = box.x / nw
    const fy = box.y / nh
    const fw = box.width / nw
    const fh = box.height / nh
    const cx = fx + fw / 2
    const cy = fy + fh / 2
    const scale = 2.65
    let w = fw * scale
    let h = fh * scale * 1.15
    const portraitAspect = 3 / 4
    if (w / h > portraitAspect) {
      h = w / portraitAspect
    } else {
      w = h * portraitAspect
    }
    const yBias = fh * 0.35
    return clampNormalizedRect(
      expandRectToAspect(
        {
          x: cx - w / 2,
          y: cy - h / 2 + yBias * 0.15,
          w,
          h,
        },
        portraitAspect,
      ),
    )
  } catch {
    return null
  }
}
